import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message, Dropdown, Form, Input } from 'antd';
import {
  LogoutOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  LockOutlined,
  NotificationOutlined,
  ControlOutlined,
  GiftOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import BroadcastInbox from '@/components/broadcast/BroadcastInbox';
import { useUserStore } from '@/stores/userStore';
import { authChangePassword } from '@/services/auth';
import { animate } from 'animejs';
import styles from './Dashboard.module.css';
import DynamicModuleCards from './sub-channels/DynamicModuleCards';
import { cabinConfigListEnabled, type CabinCard } from '@/services/cabinConfig';

// 舱室配置
const cabinConfigs = [
  {
    id: 'collaboration',
    name: '战术协调中心',
  },
  {
    id: 'strategy',
    name: '武器研究学院',
  },
  {
    id: 'system',
    name: '工程装备车间',
  },
  {
    id: 'holographic',
    name: '信息监控中心',
  },
  {
    id: 'finance',
    name: '资源规划中心',
  },
  {
    id: 'personal',
    name: '知识索引仓库',
  },
  {
    id: 'lifesupport',
    name: '生命维持中心',
  },
];

function Dashboard() {
  const navigate = useNavigate();
  const { userInfo, logout } = useUserStore();
  const starfieldRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const mainAreaRef = useRef<HTMLDivElement>(null);
  const [selectedCabin, setSelectedCabin] = useState<string>('collaboration'); // 默认选中第一项
  const [cabinCards, setCabinCards] = useState<Record<string, CabinCard[]>>({});
  const [cardsLoaded, setCardsLoaded] = useState(false);
  const [starTime, setStarTime] = useState('');
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdForm] = Form.useForm();

  // 获取用户显示名称
  const getUserDisplayName = () => {
    if (!userInfo) return '访客';
    return userInfo.realName || userInfo.username || userInfo.email || '用户';
  };

  // 伊敦星历计时器
  useEffect(() => {
    const startDate = new Date('2022-06-30T00:00:00');

    const updateStarTime = () => {
      const now = new Date();
      const diff = now.getTime() - startDate.getTime();
      const years = (diff / (1000 * 60 * 60 * 24 * 365.25)).toFixed(8);

      setStarTime(`伊敦星历 ${years}年`);
    };

    updateStarTime();
    const timer = setInterval(updateStarTime, 1000);

    return () => clearInterval(timer);
  }, []);

  // 创建星空背景
  useEffect(() => {
    if (!starfieldRef.current) return;

    const starfield = starfieldRef.current;
    const starCount = 100; // 比登录页少一些，更简约

    for (let i = 0; i < starCount; i++) {
      const star = document.createElement('div');
      star.className = styles.star;
      const size = Math.random() * 2;
      star.style.width = `${size}px`;
      star.style.height = `${size}px`;
      star.style.left = `${Math.random() * 100}%`;
      star.style.top = `${Math.random() * 100}%`;
      star.style.animationDelay = `${Math.random() * 3}s`;
      starfield.appendChild(star);
    }

    return () => {
      starfield.innerHTML = '';
    };
  }, []);

  // 入场动画
  useEffect(() => {
    // HUD角落动画
    const hudCorners = document.querySelectorAll(`.${styles.hudCorner}`);
    if (hudCorners.length > 0) {
      animate(hudCorners, {
        opacity: [0, 1],
        scale: [0.8, 1],
        duration: 1000,
        delay: (_el, i) => 100 * i,
        ease: 'outQuad',
      });
    }

    // 侧边栏入场动画
    if (sidebarRef.current) {
      animate(sidebarRef.current, {
        opacity: [0, 1],
        translateX: [-50, 0],
        duration: 800,
        ease: 'outQuad',
        delay: 200,
      });
    }

    // 主区域入场动画
    if (mainAreaRef.current) {
      animate(mainAreaRef.current, {
        opacity: [0, 1],
        translateX: [50, 0],
        duration: 800,
        ease: 'outQuad',
        delay: 400,
      });
    }

    // 菜单项入场动画
    const menuItems = document.querySelectorAll(`.${styles.cabinMenuItem}`);
    if (menuItems.length > 0) {
      animate(menuItems, {
        opacity: [0, 1],
        translateX: [-20, 0],
        duration: 600,
        delay: (_el, i) => 600 + i * 80,
        ease: 'outQuad',
      });
    }
  }, []);

  // 拉取舱室卡片配置
  useEffect(() => {
    cabinConfigListEnabled()
      .then((cards) => {
        const grouped: Record<string, CabinCard[]> = {};
        cards.forEach((card) => {
          if (!grouped[card.cabinId]) grouped[card.cabinId] = [];
          grouped[card.cabinId].push(card);
        });
        setCabinCards(grouped);
        setCardsLoaded(true);
        // 自动定位到第一个有卡片的舱室
        const firstCabinWithCards = cabinConfigs.find((c) => grouped[c.id]?.length > 0);
        if (firstCabinWithCards) {
          setSelectedCabin(firstCabinWithCards.id);
        }
      })
      .catch(() => {
        setCardsLoaded(true);
      });
  }, []);

  // 处理舱室点击
  const handleCabinClick = (cabinId: string) => {
    if (selectedCabin === cabinId) return;

    // 点击动画
    const clickedItem = document.querySelector(`[data-cabin-id="${cabinId}"]`);
    if (clickedItem) {
      animate(clickedItem, {
        scale: [1, 0.95, 1],
        duration: 300,
        ease: 'inOutQuad',
      });
    }

    // 淡出淡入主区域内容
    if (mainAreaRef.current) {
      animate(mainAreaRef.current, {
        opacity: [1, 0],
        duration: 200,
        ease: 'outQuad',
        onComplete: () => {
          setSelectedCabin(cabinId);
          setTimeout(() => {
            if (mainAreaRef.current) {
              animate(mainAreaRef.current, {
                opacity: [0, 1],
                duration: 200,
                ease: 'inQuad',
              });
            }
          }, 50);
        },
      });
    }
  };

  // 处理卡片点击
  const handleCardClick = (card: CabinCard) => {
    if (!card.url) return;
    if (card.url.startsWith('/')) {
      navigate(card.url);
      return;
    }
    window.open(card.url, '_blank');
  };

  // 处理退出登录
  const handleLogout = () => {
    Modal.confirm({
      title: '确认退出',
      icon: <ExclamationCircleOutlined />,
      content: '确定要退出登录吗？',
      okText: '确定',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => {
        // 退出动画
        if (sidebarRef.current) {
          animate(sidebarRef.current, {
            opacity: [1, 0],
            scale: [1, 0.9],
            duration: 500,
            ease: 'inBack',
            onComplete: () => {
              logout();
              message.success('已退出登录');
              navigate('/login', { replace: true });
            },
          });
        }
      },
    });
  };

  // 修改密码提交
  const handlePasswordChange = async () => {
    try {
      const values = await pwdForm.validateFields();
      if (values.newPassword !== values.confirmPassword) {
        message.error('两次输入的新密码不一致');
        return;
      }
      setPwdLoading(true);
      await authChangePassword({
        identifier: userInfo?.id || userInfo?.email || userInfo?.phone || '',
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      });
      message.success('密码修改成功');
      setPwdModalOpen(false);
      pwdForm.resetFields();
    } catch (err: any) {
      // authChangePassword 内部已通过 request 拦截器 message.error，无需重复提示
    } finally {
      setPwdLoading(false);
    }
  };

  // 用户卡片自定义菜单
  const isAdmin = useUserStore((s) => s.isAdmin);
  const hasPermission = useUserStore((s) => s.hasPermission);
  const renderUserMenu = () => (
    <div className={styles.cockpitMenu}>
      <div className={styles.cockpitMenuHeader}>
        <span className={styles.cockpitMenuTitle}>CREW · {userInfo?.id || 'N/A'}</span>
      </div>
      <div className={styles.cockpitMenuDivider} />
      {isAdmin && (
        <>
          <div
            className={styles.cockpitMenuItem}
            onClick={() => navigate('/control-center')}
          >
            <ControlOutlined className={styles.cockpitMenuIcon} />
            <span>星舰控制中心</span>
          </div>
          <div className={styles.cockpitMenuDivider} />
          <div
            className={styles.cockpitMenuItem}
            onClick={() => navigate('/broadcast-admin')}
          >
            <NotificationOutlined className={styles.cockpitMenuIcon} />
            <span>星舰广播中心</span>
          </div>
          <div className={styles.cockpitMenuDivider} />
          <div
            className={styles.cockpitMenuItem}
            onClick={() => navigate('/crew-management')}
          >
            <TeamOutlined className={styles.cockpitMenuIcon} />
            <span>星舰机组管理</span>
          </div>
          <div className={styles.cockpitMenuDivider} />
        </>
      )}
      <div
        className={styles.cockpitMenuItem}
        onClick={() => navigate('/treasure')}
      >
        <GiftOutlined className={styles.cockpitMenuIcon} />
        <span>我的宝箱</span>
      </div>
      <div className={styles.cockpitMenuDivider} />
      <div
        className={styles.cockpitMenuItem}
        onClick={() => setPwdModalOpen(true)}
      >
        <LockOutlined className={styles.cockpitMenuIcon} />
        <span>修改 AdenPassport 密码</span>
      </div>
      <div className={styles.cockpitMenuDivider} />
      <div
        className={`${styles.cockpitMenuItem} ${styles.cockpitMenuItemDanger}`}
        onClick={handleLogout}
      >
        <LogoutOutlined className={styles.cockpitMenuIcon} />
        <span>登出飞船</span>
      </div>
    </div>
  );

  return (
    <div className={styles.dashboardContainer}>
      {/* 星空背景 */}
      <div ref={starfieldRef} className={styles.starfield} />

      {/* 网格背景 */}
      <div className={styles.gridBackground} />

      {/* 主内容 - 直接显示侧边栏布局 */}
      <div className={styles.cabinLayout}>
        {/* 左侧边栏 */}
        <div ref={sidebarRef} className={styles.cabinSidebar}>
          {/* 顶部欢迎区 */}
          <div className={styles.sidebarHeader}>
            <div className={styles.starCalendar}>{starTime}</div>
            <div className={styles.welcomeSection}>
              <div className={styles.welcomeInfo}>
                <div className={styles.cockpitTitle}>Aden Cockpit</div>
                <div className={styles.welcomeText}>欢迎登船 {getUserDisplayName()}</div>
              </div>
            </div>
          </div>

          {/* 舱室菜单 */}
          <div className={styles.sidebarContent}>
            <div className={styles.cabinMenu}>
              {cabinConfigs
              .filter((cabin) => cabin.id !== 'finance' || hasPermission('P00006-M002'))
              .filter((cabin) => !cardsLoaded || (cabinCards[cabin.id]?.length ?? 0) > 0)
              .map((cabin) => {
                const isActive = selectedCabin === cabin.id;
                return (
                  <div
                    key={cabin.id}
                    data-cabin-id={cabin.id}
                    className={`${styles.cabinMenuItem} ${isActive ? styles.active : ''}`}
                    onClick={() => handleCabinClick(cabin.id)}
                  >
                    <div className={styles.cabinMenuName}>{cabin.name}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 底部用户信息区 */}
          <div className={styles.sidebarFooter}>
            <Dropdown dropdownRender={renderUserMenu} trigger={['click']} placement="topLeft">
              <div className={styles.userCard}>
                <div className={styles.userCardAvatar}>
                  <UserOutlined />
                </div>
                <div className={styles.userCardInfo}>
                  <div className={styles.userCardName}>{getUserDisplayName()}</div>
                  <div className={styles.userId}>ID · {userInfo?.id || 'N/A'}</div>
                </div>
                <div className={styles.userCardChevron}>▲</div>
              </div>
            </Dropdown>
          </div>
        </div>

        {/* 右侧主区域 */}
        <div ref={mainAreaRef} className={styles.cabinMainArea}>
          <div className={styles.cabinHeader}>
            <div className={styles.breadcrumb}>
              <span className={styles.breadcrumbItem}>AdenCockpit</span>
              <span className={styles.breadcrumbSeparator}>/</span>
              <span className={styles.breadcrumbItem}>
                {cabinConfigs.find((c) => c.id === selectedCabin)?.name}
              </span>
            </div>
            <BroadcastInbox dark />
          </div>
          <div className={styles.cabinContent}>
            <DynamicModuleCards
              cards={cabinCards[selectedCabin] ?? []}
              onCardClick={handleCardClick}
              emptyMessage={cardsLoaded ? '您暂未获得本舱室使用权限' : undefined}
            />
          </div>
        </div>
      </div>
      {/* 修改密码 Modal */}
      <Modal
        title="修改 AdenPassport 密码"
        open={pwdModalOpen}
        onOk={handlePasswordChange}
        onCancel={() => { setPwdModalOpen(false); pwdForm.resetFields(); }}
        okText="确认修改"
        cancelText="取消"
        confirmLoading={pwdLoading}
      >
        <Form form={pwdForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="oldPassword"
            label="当前密码"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Input.Password placeholder="请输入当前密码" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码长度不能少于6位' },
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            rules={[{ required: true, message: '请再次输入新密码' }]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Dashboard;
